import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';


const allUsers = [
  { id: 'u1', name: 'Dr. Sarah Johnson', role: 'Safety Manager', avatar: 'SJ' },
  { id: 'u2', name: 'Michael Chen', role: 'Warehouse Manager', avatar: 'MC' },
  { id: 'u3', name: 'Emily Rodriguez', role: 'EHS Officer', avatar: 'ER' },
  { id: 'u4', name: 'Robert Thompson', role: 'Maintenance Lead', avatar: 'RT' },
  { id: 'u5', name: 'Lisa Anderson', role: 'Process Engineer', avatar: 'LA' },
  { id: 'u6', name: 'James Wilson', role: 'Operations Manager', avatar: 'JW' },
  { id: 'u7', name: 'Maria Garcia', role: 'Quality Analyst', avatar: 'MG' },
  { id: 'u8', name: 'David Brown', role: 'Site Coordinator', avatar: 'DB' },
];

const AvatarBadge = ({ initials, size = 'sm' }) => {
  const sizeClass = size === 'sm' ? 'w-7 h-7 text-xs' : 'w-9 h-9 text-sm';
  return (
    <div className={`${sizeClass} rounded-full bg-primary/10 text-primary font-semibold flex items-center justify-center flex-shrink-0`}>
      {initials}
    </div>
  );
};

const TeamAssignmentPanel = ({ teamData, onTeamChange, readOnly = false }) => {
  const [showLeadPicker, setShowLeadPicker] = useState(false);
  const [showMemberPicker, setShowMemberPicker] = useState(false);
  const [leadSearch, setLeadSearch] = useState('');
  const [memberSearch, setMemberSearch] = useState('');

  const lead = allUsers?.find(u => u?.id === teamData?.leadId);
  const members = allUsers?.filter(u => teamData?.memberIds?.includes(u?.id));

  const filteredLeadUsers = allUsers?.filter(u =>
    u?.name?.toLowerCase()?.includes(leadSearch?.toLowerCase()) && u?.id !== teamData?.leadId
  );
  const filteredMemberUsers = allUsers?.filter(u =>
    u?.name?.toLowerCase()?.includes(memberSearch?.toLowerCase()) &&
    !teamData?.memberIds?.includes(u?.id) &&
    u?.id !== teamData?.leadId
  );

  const handleSelectLead = (user) => {
    onTeamChange?.({ ...teamData, leadId: user?.id });
    setShowLeadPicker(false);
    setLeadSearch('');
  };

  const handleAddMember = (user) => {
    onTeamChange?.({ ...teamData, memberIds: [...(teamData?.memberIds || []), user?.id] });
    setMemberSearch('');
  };

  const handleRemoveMember = (userId) => {
    onTeamChange?.({ ...teamData, memberIds: teamData?.memberIds?.filter(id => id !== userId) });
  };

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center gap-2 mb-5">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Icon name="Users" size={18} className="text-primary" />
        </div>
        <h3 className="text-base font-semibold text-foreground">Investigation Team</h3>
      </div>

      {/* Investigation Lead */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-foreground">Investigation Lead</label>
          {!readOnly && (
            <button
              onClick={() => setShowLeadPicker(!showLeadPicker)}
              className="text-xs text-primary hover:underline flex items-center gap-1"
            >
              <Icon name="Edit2" size={12} />
              {lead ? 'Change' : 'Assign'}
            </button>
          )}
        </div>

        {lead ? (
          <div className="flex items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg">
            <AvatarBadge initials={lead?.avatar} size="md" />
            <div>
              <p className="text-sm font-semibold text-foreground">{lead?.name}</p>
              <p className="text-xs text-muted-foreground">{lead?.role}</p>
            </div>
            <span className="ml-auto px-2 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded-full">Lead</span>
          </div>
        ) : (
          <div className="p-3 border border-dashed border-border rounded-lg text-center text-sm text-muted-foreground">
            No lead assigned yet
          </div>
        )}

        {showLeadPicker && !readOnly && (
          <div className="mt-2 border border-border rounded-lg bg-card shadow-lg z-10">
            <div className="p-2 border-b border-border">
              <input
                type="text"
                placeholder="Search users..."
                value={leadSearch}
                onChange={e => setLeadSearch(e?.target?.value)}
                className="w-full text-sm px-3 py-1.5 bg-muted rounded-md border-0 outline-none text-foreground placeholder:text-muted-foreground"
              />
            </div>
            <div className="max-h-48 overflow-y-auto">
              {filteredLeadUsers?.map(user => (
                <button
                  key={user?.id}
                  onClick={() => handleSelectLead(user)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted transition-colors text-left"
                >
                  <AvatarBadge initials={user?.avatar} />
                  <div>
                    <p className="text-sm font-medium text-foreground">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.role}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Team Members */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-foreground">
            Team Members <span className="text-muted-foreground font-normal">({members?.length})</span>
          </label>
          {!readOnly && (
            <button
              onClick={() => setShowMemberPicker(!showMemberPicker)}
              className="text-xs text-primary hover:underline flex items-center gap-1"
            >
              <Icon name="Plus" size={12} />
              Add Member
            </button>
          )}
        </div>

        {showMemberPicker && !readOnly && (
          <div className="mb-2 border border-border rounded-lg bg-card shadow-lg">
            <div className="p-2 border-b border-border">
              <input
                type="text"
                placeholder="Search users..."
                value={memberSearch}
                onChange={e => setMemberSearch(e?.target?.value)}
                className="w-full text-sm px-3 py-1.5 bg-muted rounded-md border-0 outline-none text-foreground placeholder:text-muted-foreground"
              />
            </div>
            <div className="max-h-40 overflow-y-auto">
              {filteredMemberUsers?.map(user => (
                <button
                  key={user?.id}
                  onClick={() => handleAddMember(user)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted transition-colors text-left"
                >
                  <AvatarBadge initials={user?.avatar} />
                  <div>
                    <p className="text-sm font-medium text-foreground">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.role}</p>
                  </div>
                  <Icon name="Plus" size={14} className="ml-auto text-primary" />
                </button>
              ))}
              {filteredMemberUsers?.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-3">No more users to add</p>
              )}
            </div>
          </div>
        )}

        <div className="space-y-2">
          {members?.length === 0 ? (
            <div className="p-3 border border-dashed border-border rounded-lg text-center text-sm text-muted-foreground">
              No team members assigned
            </div>
          ) : (
            members?.map(member => (
              <div key={member?.id} className="flex items-center gap-3 p-2.5 bg-muted/40 rounded-lg">
                <AvatarBadge initials={member?.avatar} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{member?.name}</p>
                  <p className="text-xs text-muted-foreground">{member?.role}</p>
                </div>
                {!readOnly && (
                  <button
                    onClick={() => handleRemoveMember(member?.id)}
                    className="p-1 rounded hover:bg-red-100 hover:text-red-600 text-muted-foreground transition-colors"
                  >
                    <Icon name="X" size={13} />
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamAssignmentPanel;
